import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCheckCircle,
  faInfoCircle,
  faExpand,
  faSlidersH,
  faChartLine,
} from '@fortawesome/free-solid-svg-icons';

const ParameterStabilityHero = () => (
  <div className="rounded-lg overflow-hidden shadow-2xl shadow-black/30 border border-white/[0.06] font-mono" style={{ background: '#0a0e14' }}>
    {/* Workspace tab bar */}
    <div className="flex items-center border-b border-white/[0.06] px-1" style={{ background: '#181825' }}>
      <div className="flex items-center gap-1.5 px-3 py-1.5 border-b-2 border-[#9d4edd] text-[10px] text-[#9d4edd]">
        <FontAwesomeIcon icon={faChartLine} className="text-[9px]" />
        Optimization Results
      </div>
      <div className="flex items-center gap-1.5 px-3 py-1.5 text-[10px] text-gray-500">
        Console
      </div>
    </div>

    {/* Parameter Stability card -- matches DeepStabilityPanel.tsx */}
    <div className="p-3">
      <div className="bg-[#181825] rounded-lg border border-white/[0.06] p-4 flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between mb-3 pb-2 border-b border-gray-700/50">
          <div className="flex items-center gap-2">
            <FontAwesomeIcon icon={faSlidersH} className="w-4 h-4 text-gray-400" />
            <h3 className="text-sm font-medium text-gray-300 uppercase tracking-wide">
              Parameter Stability
            </h3>
          </div>
          <div className="flex items-center gap-2">
            <div className="flex items-center gap-1.5 text-xs text-gray-300">
              <FontAwesomeIcon icon={faCheckCircle} className="w-3 h-3" />
              <span>Region Found</span>
            </div>
            <span className="p-1 text-gray-400">
              <FontAwesomeIcon icon={faExpand} className="text-xs" />
            </span>
          </div>
        </div>

        {/* Narrative */}
        <p className="text-xs text-gray-400 mb-3">Wide stable region with low sensitivity.</p>

        {/* Stability Width */}
        <div className="mb-3">
          <div className="flex items-center justify-between text-[9px] text-gray-500 uppercase tracking-wide mb-1.5">
            <span>Stability Width</span>
            <span className="text-gray-400 normal-case">Wide Stable Region</span>
          </div>
          <div className="h-3 bg-gray-700 rounded-full overflow-hidden flex">
            <div className="h-full rounded-l-full bg-gray-300" style={{ width: '55%' }} />
            <div className="h-full bg-gray-600/30" style={{ width: '45%' }} />
          </div>
          <div className="flex justify-between text-[8px] text-gray-600 mt-0.5">
            <span>fragile</span>
            <span>55%</span>
            <span>robust</span>
          </div>
        </div>

        {/* Parameter Sensitivity */}
        <div className="mb-3 space-y-1.5">
          <div className="flex items-center justify-between text-[9px] text-gray-500 uppercase tracking-wide mb-1">
            <span>Parameter Sensitivity</span>
            <span className="normal-case text-gray-300">Low Sensitivity</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-[9px] text-gray-500 w-16 truncate">fastLength</span>
            <div className="flex-1 h-1.5 bg-gray-700 rounded-full overflow-hidden">
              <div className="h-full rounded-full bg-gray-300" style={{ width: '78%' }} />
            </div>
            <span className="text-[8px] text-gray-500 w-8 text-right">78%</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-[9px] text-gray-500 w-16 truncate">slowLength</span>
            <div className="flex-1 h-1.5 bg-gray-700 rounded-full overflow-hidden">
              <div className="h-full rounded-full bg-gray-400" style={{ width: '32%' }} />
            </div>
            <span className="text-[8px] text-gray-500 w-8 text-right">32%</span>
          </div>
        </div>

        {/* Classification Flags */}
        <div className="grid grid-cols-3 gap-2 mb-3">
          <div className="text-center">
            <div className="text-[9px] text-gray-600 uppercase tracking-wide mb-0.5">Class</div>
            <div className="text-xs font-medium text-gray-300">Stable Plateau</div>
          </div>
          <div className="text-center border-x border-gray-700/50">
            <div className="text-[9px] text-gray-600 uppercase tracking-wide mb-0.5">Sensitivity</div>
            <div className="text-xs font-medium text-gray-300">Low</div>
          </div>
          <div className="text-center">
            <div className="text-[9px] text-gray-600 uppercase tracking-wide mb-0.5">Confidence</div>
            <div className="text-xs font-medium text-gray-300">High</div>
          </div>
        </div>

        {/* Key Metrics */}
        <div className="space-y-1.5 mb-3">
          <div className="flex items-center justify-between">
            <span className="text-xs text-gray-500">Stability Score</span>
            <span className="text-base font-bold text-gray-200">55%</span>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-xs text-gray-500">Parameters</span>
            <span className="text-xs text-gray-400">All stable</span>
          </div>
        </div>

        {/* Footer */}
        <p className="text-[10px] text-gray-600 italic pt-2 border-t border-gray-700">
          Stable parameter region confirmed.
        </p>
      </div>
    </div>

    {/* Bottom dock */}
    <div className="border-t border-white/[0.06]" style={{ background: '#181825' }}>
      <div className="flex items-center px-2 py-1 border-b border-white/[0.06]" style={{ background: '#0f1923' }}>
        <span className="text-[9px] text-[#9d4edd] border-b border-[#9d4edd] px-2 py-0.5">Output</span>
        <span className="text-[9px] text-gray-400 px-2 py-0.5">Console</span>
      </div>
      <div className="px-2 py-1.5 font-mono text-[10px] space-y-0.5">
        <div className="flex items-start gap-1.5">
          <span className="text-gray-500 select-none">[09:17:46]</span>
          <FontAwesomeIcon icon={faCheckCircle} className="text-green-400 text-[8px] mt-0.5" />
          <span className="text-green-300">Stable plateau detected: fastLength [10-45], slowLength [20-80]</span>
        </div>
        <div className="flex items-start gap-1.5">
          <span className="text-gray-500 select-none">[09:17:46]</span>
          <FontAwesomeIcon icon={faInfoCircle} className="text-cyan-400 text-[8px] mt-0.5" />
          <span className="text-cyan-300">Stability Score: 55% -- Classification: Stable Plateau</span>
        </div>
      </div>
    </div>
  </div>
);

export default ParameterStabilityHero;
